import { useRef, useState, type MouseEvent, type TouchEvent } from 'react';
import type { Task } from '../types';
import { escalatedPriority, priorityLabel, priorityStripeColor } from '../utils/priority';
import { useTeam } from '../context/TeamContext';
import { ScopeDot } from './ScopeDot';
import { AssigneeAvatar } from './AssigneeAvatar';

interface Props {
  task: Task;
  selected: boolean;
  onSelect: () => void;
  onContextMenu?: (e: MouseEvent) => void;
  onSwipeComplete?: () => void;
  onSwipeDelete?: () => void;
}

function formatDue(iso: string | undefined): string | null {
  if (!iso) return null;
  const due = new Date(iso);
  const now = new Date();
  const diffDays = Math.round(
    (due.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)
  );
  if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 7) return `${diffDays}d`;
  return due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const COMMIT_PX = 140; // swipe threshold to fire the action
const AXIS_LOCK_PX = 15; // must drag this far before we lock to an axis
const MAX_SWIPE = 200; // clamp on drag distance

export function TaskRow({
  task,
  selected,
  onSelect,
  onContextMenu,
  onSwipeComplete,
  onSwipeDelete,
}: Props) {
  const { byId, members } = useTeam();
  const eff = escalatedPriority(task.priority, task.dueDate);
  const due = formatDue(task.dueDate);
  const overdue =
    task.dueDate &&
    new Date(task.dueDate).getTime() < Date.now() &&
    task.status !== 'completed';
  const stripeColor = priorityStripeColor(eff);
  const assignee = byId(task.assigneeId);
  const showAvatar = members.length > 1 && assignee;

  const [swipeX, setSwipeX] = useState(0);
  const touchRef = useRef<{ startX: number; startY: number; axisLocked: 'x' | 'y' | null; moved: boolean } | null>(
    null
  );

  function onTouchStart(e: TouchEvent<HTMLDivElement>) {
    if (!onSwipeComplete && !onSwipeDelete) return;
    const t = e.touches[0];
    touchRef.current = { startX: t.clientX, startY: t.clientY, axisLocked: null, moved: false };
  }
  function onTouchMove(e: TouchEvent<HTMLDivElement>) {
    const s = touchRef.current;
    if (!s) return;
    const t = e.touches[0];
    const dx = t.clientX - s.startX;
    const dy = t.clientY - s.startY;
    if (!s.axisLocked && (Math.abs(dx) > AXIS_LOCK_PX || Math.abs(dy) > AXIS_LOCK_PX)) {
      // Require horizontal motion to be meaningfully dominant before committing
      // to a swipe — diagonal drags stay on the vertical axis and scroll normally.
      s.axisLocked = Math.abs(dx) > Math.abs(dy) * 1.5 ? 'x' : 'y';
    }
    if (s.axisLocked === 'x') {
      const clamped = Math.max(-MAX_SWIPE, Math.min(MAX_SWIPE, dx));
      setSwipeX(clamped);
      s.moved = true;
    }
  }
  function onTouchEnd() {
    const s = touchRef.current;
    touchRef.current = null;
    if (!s) return;
    if (s.axisLocked !== 'x') {
      setSwipeX(0);
      return;
    }
    if (swipeX <= -COMMIT_PX && onSwipeDelete) {
      setSwipeX(0);
      onSwipeDelete();
    } else if (swipeX >= COMMIT_PX && onSwipeComplete) {
      setSwipeX(0);
      onSwipeComplete();
    } else {
      setSwipeX(0);
    }
  }

  const isSwiping = Math.abs(swipeX) > 2;

  return (
    <div
      className={`task-row-wrap ${isSwiping ? 'is-swiping' : ''} ${
        swipeX > 0 ? 'swipe-right' : swipeX < 0 ? 'swipe-left' : ''
      }`}
    >
      {/* Action backgrounds behind the row */}
      <div className="task-row-swipe-bg task-row-swipe-complete" aria-hidden="true">
        <span>✓ Complete</span>
      </div>
      <div className="task-row-swipe-bg task-row-swipe-delete" aria-hidden="true">
        <span>🗑 Delete</span>
      </div>

      <div
        role="button"
        tabIndex={0}
        className={`task-row ${selected ? 'is-selected' : ''} ${
          task.status === 'completed' ? 'is-completed' : ''
        }`}
        data-completed-at={task.completedAt ?? ''}
        style={{
          ['--row-accent' as string]: stripeColor,
          transform: `translateX(${swipeX}px)`,
          transition: touchRef.current ? 'none' : 'transform 0.18s ease',
        }}
        title={
          eff !== task.priority
            ? `${priorityLabel(eff)} priority (escalated by due date from ${priorityLabel(task.priority)})`
            : `${priorityLabel(eff)} priority`
        }
        onClick={(e) => {
          // If the user just finished a swipe, suppress the synthesized click
          if (isSwiping) return;
          onSelect();
          // avoid unused var warning
          void e;
        }}
        onContextMenu={onContextMenu}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      >
        {task.pinned && (
          <span className="task-pin-corner" aria-label="Pinned" title="Pinned">
            📌
          </span>
        )}
        <ScopeDot scope={task.scope} />
        <div className="task-row-body">
          <div className="task-row-title-line">
            <div className="task-row-title">
              {task.private && <span className="task-private-lock" aria-label="Private" title="Private — only you can see this">🔒</span>}
              {task.directiveWarning && (
                <span
                  className="task-directive-warning"
                  aria-label="Directive warning"
                  title={task.directiveWarning}
                >
                  ⚠
                </span>
              )}
              <span>{task.title}</span>
            </div>
            {(due || showAvatar) && (
              <div className="task-row-right-stack">
                {showAvatar && <AssigneeAvatar member={assignee} size={18} />}
                {due && (
                  <span className={`task-row-due ${overdue ? 'is-overdue' : ''}`}>
                    {due}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
